<template>
  <div class="variable-product-tab pa-2">
    <div class="d-flex align-center mb-3 mt-3">
      <v-btn outlined color="primary" height="32" @click="addVariant">
        {{ $t("variant-product") }}
        <v-icon class="ms-1" small>mdi-plus</v-icon>
      </v-btn>
    </div>

    <draggable
      v-model="variants"
      handle=".drag-handle"
      :options="{
        animation: 200,
        direction: 'vertical',
        scrollSensitivity: 80,
      }"
      @end="onVariantsDragEnd"
    >
      <div
        v-for="(variant, index) in variants"
        :key="variant._uid"
        class="variable-product-row mb-3"
      >
        <div class="variable-product-row__main d-flex align-end flex-wrap">
          <div class="variable-product-row-index me-2">
            {{ index + 1 }}
          </div>
          <div class="variable-product-thumb me-3" @click="pickImage(index)">
            <v-avatar
              rounded
              size="68"
              color="#f5f5f5"
              class="variable-product-thumb__avatar"
            >
              <v-img v-if="variant.imagePreview" :src="variant.imagePreview" />
              <v-icon v-else color="grey lighten-1">mdi-image-outline</v-icon>
            </v-avatar>
            <input
              :ref="'variantImageInput' + index"
              type="file"
              accept="image/png,image/gif,image/jpeg"
              class="d-none"
              @change="onImageChange($event, index)"
            />
          </div>
          <div class="variable-product-fields d-flex align-center flex-wrap me-3">
            <v-text-field
              v-model="variant.name"
              :label="$t('name')"
              dense
              outlined
              hide-details
              class="variable-product-name me-3"
              clearable
              @input="onNameManualEdit(variant)"
            />

            <money
              v-model="variant.price"
              :label="$t('fixSellPrice')"
              :dense="true"
              :outlined="true"
              :solo="false"
              :clearable="true"
              :hide-details="true"
              value-when-is-empty=""
              :options="options"
              class="variable-product-price me-3"
              @input="calcPriceWithTax(variant)"
            />

            <v-btn
              color="primary"
              outlined
              height="40"
              class="variable-product-add-attr"
              @click="addAttribute(variant)"
            >
              <v-icon small class="me-1">mdi-plus</v-icon>
              {{ $t("add-attribute") }}
            </v-btn>
          </div>
          <div class="variable-product-actions d-flex align-center flex-shrink-0">
            <v-btn icon small color="error" @click="removeVariant(index)">
              <v-icon>mdi-delete-outline</v-icon>
            </v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="grey darken-1"
                  class="me-1"
                  v-bind="attrs"
                  v-on="on"
                  @click="openSettings(index)"
                >
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("more-settings") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="grey darken-1"
                  class="drag-handle"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-drag-vertical</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("move") }} </span>
            </v-tooltip>
          </div>
        </div>
        <div
          v-if="variant.attributes && variant.attributes.length"
          class="variable-product-attrs mt-3"
          :class="{
            'variable-product-attrs--scroll': variant.attributes.length > 2,
          }"
        >
          <v-row v-for="(attr, attrIndex) in variant.attributes" :key="attrIndex">
            <v-col cols="12" md="3" class="mb-0">
              <v-combobox
                v-model="attr.key"
                :items="keyItems"
                :search-input.sync="attrSearch"
                hide-selected
                :rules="[(v) => !!v || $t('is-required')]"
                :label="$t('attribute-name')"
                outlined
                value="key"
                dense
                height="40"
                small-chips
                class="variable-product-attr-input"
                @change="syncVariantName(variant)"
              >
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ $t("toCreate") }} : <strong>{{ attrSearch }}</strong
                        >. {{ $t("doEnter") }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-combobox>
            </v-col>
            <v-col cols="12" md="3" class="mb-0">
              <v-text-field
                v-model="attr.value"
                :label="$t('value')"
                height="40"
                dense
                :rules="[(v) => !!v || $t('is-required')]"
                outlined
                class="variable-product-attr-input"
                clearable
                @input="syncVariantName(variant)"
              />
            </v-col>
            <v-col cols="12" md="4" class="mb-0 d-flex align-center">
              <v-checkbox
                v-model="attr.showInName"
                :label="$t('show-attribute-in-product-name')"
                hide-details
                dense
                class="mt-0"
                @change="syncVariantName(variant)"
              />
            </v-col>
            <v-col cols="12" md="2" class="mb-0 d-flex align-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    color="pink"
                    icon
                    small
                    v-bind="attrs"
                    class="ms-2"
                    v-on="on"
                    @click="removeAttribute(variant, attrIndex)"
                  >
                    <v-icon color="pink" style="font-size: 26px">mdi-close</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("remove") }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </div>
      </div>
    </draggable>
    <v-dialog
      v-model="settingsDialog"
      :fullscreen="isMobile"
      :max-width="isMobile ? undefined : 920"
      scrollable
    >
      <v-card v-if="activeVariant" class="variable-product-dialog">
        <v-col class="d-flex justify-space-between pb-0">
          <div class="variable-product-dialog__title text-right">
            <div class="text-body-1 font-weight-medium">
              {{ $t("variable-product-settings") }}
            </div>

            <div class="text-caption grey--text">
              <div>
                {{ $t("main-product-name") }}:
                {{ activeVariant.baseName || productName }}
              </div>
              <div v-if="activeVariantAttributes">
                {{ $t("attributes") }}:
                {{ activeVariantAttributes }}
              </div>
            </div>
          </div>
          <v-btn icon color="grey darken-2" @click="settingsDialog = false">
            <v-icon class="close-hover" small> mdi-close </v-icon>
          </v-btn>
        </v-col>
        <v-divider />
        <v-tabs v-model="settingsTab" class="px-2" grow>
          <v-tab>{{ $t("variant-differentiate") }}</v-tab>
          <v-tab>{{ $t("tax&priceing") }}</v-tab>
          <v-tab>{{ $t("otherBarcodes") }} / {{ $t("tag") }}</v-tab>
          <v-tab>{{ $t("pricing") }} {{ $t("advance") }}</v-tab>
          <v-tab>{{ $t("otherSetting") }}</v-tab>
        </v-tabs>

        <v-card-text class="variable-product-dialog__body">
          <v-tabs-items v-model="settingsTab">
            <v-tab-item>
              <div class="variable-product-dialog__panel">
                <v-row>
                  <v-col cols="12" md="1" class="pe-4">
                    <div class="variable-product-thumb" @click="pickImage(activeIndex)">
                      <v-avatar rounded size="82" color="#f5f5f5">
                        <v-img
                          v-if="activeVariant.imagePreview"
                          :src="activeVariant.imagePreview"
                        />
                        <v-icon v-else color="grey">mdi-image-outline</v-icon>
                      </v-avatar>
                    </div>
                  </v-col>
                  <v-col
                    cols="12"
                    md="11"
                    class="ps-4"
                    :class="
                      isMobile
                        ? 'variable-product-dialog__inputs-mobile'
                        : 'variable-product-dialog__inputs-desktop'
                    "
                  >
                    <v-row>
                      <v-col cols="12" md="6" sm="6" class="mb-0">
                        <v-text-field
                          v-model="activeVariant.baseName"
                          :label="$t('name')"
                          height="20"
                          dense
                          outlined
                          hide-details="auto"
                          class="mb-1 dense"
                          clearable
                          @input="syncVariantName(activeVariant)"
                        />
                      </v-col>
                      <v-col cols="12" md="3" class="mb-0">
                        <v-text-field
                          v-model="activeVariant.sku"
                          :label="$t('sku')"
                          height="20"
                          dense
                          outlined
                          hide-details="auto"
                          class="mb-1 dense"
                          clearable
                        />
                      </v-col>
                      <v-col cols="12" md="3" class="mb-0">
                        <v-text-field
                          v-model="activeVariant.code"
                          :label="$t('code')"
                          height="20"
                          dense
                          outlined
                          hide-details="auto"
                          class="mb-1 dense"
                          clearable
                        />
                      </v-col>
                      <v-col cols="12" md="3" class="mb-0">
                        <v-text-field
                          v-model="activeVariant.custom_code"
                          :label="$t('Custom financial ID')"
                          height="20"
                          dense
                          outlined
                          hide-details="auto"
                          class="mb-1 dense"
                          clearable
                        />
                      </v-col>
                      <v-col cols="12" md="3" class="mb-0">
                        <v-text-field
                          v-model="activeVariant.displayPriority"
                          :label="$t('displayPriority')"
                          height="20"
                          dense
                          outlined
                          hide-details="auto"
                          type="number"
                          class="mb-1 dense"
                          clearable
                        />
                      </v-col>
                      <v-col cols="12" md="3" class="mb-0 d-flex align-center">
                        <v-checkbox
                          v-model="activeVariant.status"
                          :label="
                            $t('status') +
                            ' : ' +
                            (activeVariant.status ? $t('active') : $t('deactive'))
                          "
                          value="active"
                          hide-details
                          class="mt-0"
                        />
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>

                <v-divider class="my-4" />
                <v-btn
                  color="primary"
                  text
                  small
                  class="mb-2"
                  @click="addAttribute(activeVariant)"
                >
                  {{ $t("add-attribute") }}
                  <v-icon small class="ms-1">mdi-plus</v-icon>
                </v-btn>
                <div
                  class="variable-product-dialog-attrs"
                  :class="{
                    'variable-product-dialog-attrs--scroll':
                      activeVariant.attributes.length > 4,
                  }"
                >
                  <v-row
                    v-for="(attr, attrIndex) in activeVariant.attributes"
                    :key="'dlg-attr-' + attrIndex"
                  >
                    <v-col cols="12" md="3" class="mb-0">
                      <v-combobox
                        v-model="attr.key"
                        :items="keyItems"
                        :search-input.sync="attrSearch"
                        hide-selected
                        :rules="[(v) => !!v || $t('is-required')]"
                        :label="$t('attribute-name')"
                        outlined
                        value="key"
                        dense
                        height="40"
                        small-chips
                        class="variable-product-attr-input"
                        @change="syncVariantName(activeVariant)"
                      >
                        <template v-slot:no-data>
                          <v-list-item>
                            <v-list-item-content>
                              <v-list-item-title>
                                {{ $t("toCreate") }} : <strong>{{ attrSearch }}</strong
                                >. {{ $t("doEnter") }}
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </template>
                      </v-combobox>
                    </v-col>
                    <v-col cols="12" md="3" class="mb-0">
                      <v-text-field
                        v-model="attr.value"
                        :label="$t('value')"
                        height="40"
                        dense
                        :rules="[(v) => !!v || $t('is-required')]"
                        outlined
                        class="variable-product-attr-input"
                        clearable
                        @input="syncVariantName(activeVariant)"
                      />
                    </v-col>
                    <v-col cols="12" md="4" class="mb-0 d-flex align-center">
                      <v-checkbox
                        v-model="attr.showInName"
                        :label="$t('show-attribute-in-product-name')"
                        hide-details
                        dense
                        class="mt-0"
                        @change="syncVariantName(activeVariant)"
                      />
                    </v-col>
                    <v-col cols="12" md="2" class="mb-0 d-flex align-center">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            color="pink"
                            icon
                            small
                            v-bind="attrs"
                            class="ms-2"
                            v-on="on"
                            @click="removeAttribute(activeVariant, attrIndex)"
                          >
                            <v-icon color="pink" style="font-size: 26px"
                              >mdi-close</v-icon
                            >
                          </v-btn>
                        </template>
                        <span>{{ $t("remove") }}</span>
                      </v-tooltip>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-tab-item>
            <v-tab-item>
              <div class="variable-product-dialog__panel">
                <v-row>
                  <v-col cols="12" md="3" class="pt-6">
                    <v-text-field
                      v-model="activeVariant.taxid"
                      :label="$t('taxisProduct')"
                      dense
                      outlined
                      type="number"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" md="3" class="pt-6">
                    <v-text-field
                      v-model="activeVariant.taxPercent"
                      :label="$t('taxPercent')"
                      dense
                      outlined
                      type="number"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" md="3" class="pt-6">
                    <money
                      v-model="activeVariant.price"
                      :label="$t('fixSellPrice')"
                      :dense="true"
                      :outlined="true"
                      :solo="false"
                      :clearable="true"
                      value-when-is-empty=""
                      :options="options"
                      @input="calcPriceWithTax(activeVariant)"
                    />
                  </v-col>
                  <v-col cols="12" md="3" class="pt-6">
                    <money
                      v-model="activeVariant.priceWithTax"
                      :label="$t('priceWithTax')"
                      :dense="true"
                      :outlined="true"
                      :solo="false"
                      :clearable="true"
                      value-when-is-empty=""
                      :options="options"
                      @input="calcPrice(activeVariant)"
                    />
                  </v-col>
                  <v-col cols="12" md="3" class="pt-6">
                    <money
                      v-model="activeVariant.discount"
                      :label="$t('discount')"
                      :dense="true"
                      :outlined="true"
                      :solo="false"
                      :clearable="true"
                      value-when-is-empty=""
                      :options="options"
                    />
                  </v-col>
                  <v-col cols="12" md="3" class="pt-6">
                    <v-text-field
                      v-model="activeVariant.discountPercent"
                      :label="$t('discountPercent')"
                      dense
                      outlined
                      type="number"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" md="3" class="d-flex align-center pt-6">
                    <v-checkbox
                      v-model="activeVariant.isKalaBarg"
                      :label="$t('isKalaBarg')"
                      hide-details
                      class="mt-0"
                    />
                  </v-col>
                </v-row>
              </div>
            </v-tab-item>
            <v-tab-item>
              <div class="variable-product-dialog__panel">
                <v-row>
                  <v-col cols="12" md="4" class="mb-0">
                    <v-text-field
                      v-model="activeVariant.barcode"
                      :label="$t('barcode')"
                      dense
                      outlined
                      hide-details
                      clearable
                      class="mb-1 dense"
                    />
                  </v-col>
                  <v-col cols="12" md="4" class="mb-0">
                    <tag
                      v-model="activeVariant.tag"
                      :solo="false"
                      :input="activeVariant.tag"
                    />
                  </v-col>
                </v-row>
                <v-row class="mt-8 mb-2">
                  <v-col cols="12" class="py-0">
                    <v-btn
                      color="primary"
                      small
                      outlined
                      height="32"
                      @click="addBarcode(activeVariant)"
                    >
                      {{ $t("addBarcode") }}
                      <v-icon class="ms-2" small>mdi-plus</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row v-for="(item, i) in activeVariant.barcodes" :key="'barcode-' + i">
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="item.value"
                      :label="$t('barcode')"
                      dense
                      outlined
                      :rules="[(v) => !!v || $t('is-required')]"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-btn
                      color="pink"
                      small
                      outlined
                      height="32"
                      @click="removeBarcode(activeVariant, item)"
                    >
                      <v-icon>mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
            </v-tab-item>
            <v-tab-item>
              <div class="variable-product-dialog__panel">
                <pricing
                  v-model="activeVariant.pricing"
                  :costs="costs"
                  :method="priceMethod"
                  :value="activeVariant.pricing"
                />
              </div>
            </v-tab-item>
            <v-tab-item>
              <div class="variable-product-dialog__panel">
                <v-row class="mb-5">
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="activeVariant.length"
                      :label="$t('length')"
                      dense
                      outlined
                      type="number"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="activeVariant.width"
                      :label="$t('width')"
                      dense
                      outlined
                      type="number"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="activeVariant.height"
                      :label="$t('height')"
                      dense
                      outlined
                      type="number"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="activeVariant.weight"
                      :label="$t('weight') + '(kg)'"
                      dense
                      outlined
                      type="number"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="activeVariant.orderPoint"
                      :label="$t('orderPoint')"
                      dense
                      outlined
                      type="number"
                      clearable
                    />
                  </v-col>
                </v-row>
                <template v-if="showInitialStock">
                  <v-divider class="my-3" />
                  <v-row class="mb-2" style="height: 28px">
                    <v-subheader>
                      {{ $t("initial-stock") }}
                    </v-subheader>
                    <v-tooltip v-if="variantInitialCheck(activeVariant)" bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          v-bind="attrs"
                          icon
                          small
                          class="float-left mt-2"
                          v-on="on"
                          @click="addInitialStock(activeVariant)"
                        >
                          <v-icon color="teal" style="font-size: 22px">
                            mdi-plus-box-outline
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("addInitialStock") }}</span>
                    </v-tooltip>
                  </v-row>
                  <div class="variable-product-scroll-box">
                    <v-row
                      v-for="(initial, i) in activeVariant.initial_stocks"
                      :key="'stock-' + i"
                      class="mb-2"
                    >
                      <v-col cols="12" sm="3">
                        <v-text-field
                          v-model="initial.stock"
                          :label="$t('count')"
                          height="20"
                          dense
                          outlined
                          class="mb-1 dense"
                          clearable
                          :disabled="!variantInitialCheck(activeVariant)"
                          type="number"
                          @input="validateStock(initial)"
                        />
                      </v-col>
                      <v-col cols="12" sm="3" md="3" class="mb-1">
                        <money
                          v-model="initial.price"
                          :label="$t('fee')"
                          :dense="true"
                          :outlined="true"
                          :solo="false"
                          :clearable="true"
                          value-when-is-empty="0"
                          :disabled="!variantInitialCheck(activeVariant)"
                          :options="options"
                        />
                      </v-col>
                      <v-col
                        class="mb-0 pb-0"
                        cols="12"
                        md="4"
                        sm="4"
                        style="height: 55px"
                      >
                        <autocomplete
                          v-model="initial.entrepot"
                          :rules="entrepotRuls(initial)"
                          :title="$t('entrepot')"
                          :hide-details="false"
                          type="entrepot"
                          :disabled="!variantInitialCheck(activeVariant)"
                          @input="checkEntrepot(activeVariant, initial, i)"
                        />
                      </v-col>
                      <v-col
                        class="mb-0 pb-0"
                        cols="12"
                        md="2"
                        sm="2"
                        style="height: 55px"
                      >
                        <v-btn
                          v-show="variantInitialCheck(activeVariant)"
                          color="pink"
                          small
                          outlined
                          height="32"
                          @click="removeInitialStock(activeVariant, i)"
                        >
                          <v-icon> mdi-trash-can-outline </v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </div>
                </template>
                <v-divider class="my-3" />
                <v-row class="mb-2" style="height: 28px">
                  <v-subheader>
                    {{ $t("investor") }}
                  </v-subheader>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        icon
                        small
                        class="float-left mt-2"
                        v-on="on"
                        @click="addInvestorRow(activeVariant)"
                      >
                        <v-icon color="teal" style="font-size: 22px">
                          mdi-plus-box-outline
                        </v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("addInvestor") }}</span>
                  </v-tooltip>
                </v-row>
                <div class="variable-product-scroll-box">
                  <v-row
                    v-for="(item, i) in activeVariant.investors"
                    :key="'investor-' + i"
                  >
                    <v-col class="mb-0 pb-0" cols="12" md="3" style="height: 55px">
                      <autocomplete
                        v-model="item.investor"
                        :rules="[(v) => !!v || $t('is-required')]"
                        :title="$t('investor')"
                        :value="item.investor"
                        type="person"
                        :new-btn="true"
                        :data="JSON.stringify({ type: 'investor' })"
                        @input="changeInvestor(item)"
                      />
                    </v-col>
                    <v-col cols="12" md="3" class="mb-0">
                      <v-text-field
                        v-model="item.investorSellPercent"
                        :label="$t('investorSellPercent')"
                        height="20"
                        dense
                        :rules="[(v) => !!v || $t('is-required')]"
                        outlined
                        type="number"
                        class="mb-1 dense"
                        clearable
                      />
                    </v-col>
                    <v-col cols="12" md="3" class="mb-0">
                      <v-text-field
                        v-model="item.investorReturnSellPercent"
                        :label="$t('investorReturnSellPercent')"
                        height="20"
                        dense
                        :rules="[(v) => !!v || $t('is-required')]"
                        outlined
                        type="number"
                        class="mb-1 dense"
                        clearable
                      />
                    </v-col>
                    <v-col cols="12" md="3" class="">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            color="pink"
                            icon
                            small
                            v-bind="attrs"
                            class="ms-2"
                            v-on="on"
                            @click="removeInvestor(activeVariant, item)"
                          >
                            <v-icon color="pink" style="font-size: 22px">
                              mdi-close-box
                            </v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("remove") }}</span>
                      </v-tooltip>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>

        <v-divider />
        <v-card-actions class="pa-3">
          <v-btn color="primary" height="32" @click="settingsDialog = false">
            {{ $t("confirm") }}
          </v-btn>
          <v-btn color="secondary" outlined height="32" @click="settingsDialog = false">
            {{ $t("cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="snakbar"
      :color="snakbarColor ? snakbarColor : 'primary'"
      top
      style="z-index: 1000000000000 !important"
    >
      {{ snakbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snakbar = !snakbar">
          {{ $t("close") }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import General from "../../../../../Mixins/general";
import Pricing from "../pricing";
import draggable from "vuedraggable";

let variantUid = 1;

function createEmptyVariant(baseName = "") {
  return {
    _uid: variantUid++,
    baseName: baseName || "",
    name: baseName || "",
    nameManual: false,
    attributes: [],
    imageFile: null,
    imagePreview: null,
    sku: null,
    code: null,
    custom_code: null,
    displayPriority: null,
    status: "active",
    taxid: null,
    taxPercent: 10,
    price: null,
    priceWithTax: 0,
    discount: null,
    discountPercent: 0,
    isKalaBarg: false,
    barcode: null,
    barcodes: [],
    tag: null,
    pricing: null,
    length: null,
    width: null,
    height: null,
    weight: null,
    orderPoint: null,
    investors: [],
    initial_stocks: [],
  };
}

export default {
  name: "VariableProductTab",
  components: {
    Pricing,
    draggable,
    Tag: () => import("../../../components/general/Tag/tag"),
    autocomplete: () => import("../../../components/general/patial/autocomplete"),
  },
  mixins: [General],
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    productName: {
      type: String,
      default: "",
    },
    costs: {
      default: null,
    },
    priceMethod: {
      type: String,
      default: "max",
    },
    initialCheck: {
      type: Boolean,
      default: true,
    },
    productType: {
      type: String,
      default: "product",
    },
  },
  data() {
    return {
      variants: [],
      settingsDialog: false,
      settingsTab: 0,
      activeIndex: -1,
      activeUid: null,
      keyItems: [],
      attrSearch: null,
      attrKeyTimer: null,
    };
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.name === "sm" || this.$vuetify.breakpoint.xs;
    },
    activeVariant() {
      if (this.activeIndex < 0) return null;
      return this.variants[this.activeIndex] || null;
    },

    activeVariantAttributes() {
      if (!this.activeVariant) return "";
      return (this.activeVariant.attributes || [])
        .filter((a) => a && a.value)
        .map((a) => String(a.value).trim())
        .filter(Boolean)
        .join(" - ");
    },
    showInitialStock() {
      return this.$store.state.isMainYear && this.productType == "product";
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (Array.isArray(val) && val !== this.variants) {
          this.variants = val.map((item) => ({
            ...createEmptyVariant(),
            ...item,
            _uid: item._uid || variantUid++,
            attributes: item.attributes ? [...item.attributes] : [],
            barcodes: item.barcodes ? [...item.barcodes] : [],
            investors: item.investors ? item.investors.map((inv) => ({ ...inv })) : [],
            initial_stocks: item.initial_stocks
              ? item.initial_stocks.map((stock) => ({ ...stock }))
              : [],
          }));
        }
      },
    },
    variants: {
      deep: true,
      handler(val) {
        this.$emit("input", val);
      },
    },
    productName(val) {
      const name = val || "";
      this.variants.forEach((variant) => {
        if (variant.nameManual) return;
        variant.baseName = name;
        this.syncVariantName(variant);
      });
    },
    attrSearch() {
      this.getAttributeKeys();
    },
  },
  mounted() {
    this.getAttributeKeys();
  },
  methods: {
    getAttributeKeys() {
      const app = this;
      clearTimeout(app.attrKeyTimer);
      app.attrKeyTimer = setTimeout(function () {
        app.$axios
          .get(app.$endpoint + "getdetail", {
            params: {
              select: "productVariantFields",
              search: app.attrSearch,
            },
          })
          .then((res) => {
            app.keyItems = res.data;
          })
          .catch((err) => {
            app.serverErrorHandeler(err);
          });
      }, 1000);
    },
    addVariant() {
      const variant = createEmptyVariant(this.productName || "");
      this.variants.push(variant);
    },
    removeVariant(index) {
      this.variants.splice(index, 1);
      if (this.activeIndex === index) {
        this.settingsDialog = false;
        this.activeIndex = -1;
        this.activeUid = null;
      } else if (this.activeUid != null) {
        this.syncActiveIndex();
      }
    },
    onVariantsDragEnd() {
      this.syncActiveIndex();
    },
    syncActiveIndex() {
      if (this.activeUid == null) return;
      const idx = this.variants.findIndex((v) => v._uid === this.activeUid);
      this.activeIndex = idx;
      if (idx < 0) {
        this.settingsDialog = false;
        this.activeUid = null;
      }
    },
    addAttribute(variant) {
      if (!variant.attributes) this.$set(variant, "attributes", []);
      variant.attributes.push({ key: null, value: null, showInName: true });
    },
    removeAttribute(variant, attrIndex) {
      variant.attributes.splice(attrIndex, 1);
      this.syncVariantName(variant);
    },
    composeName(variant) {
      const parts = [];
      if (variant.baseName) parts.push(String(variant.baseName).trim());
      (variant.attributes || []).forEach((attr) => {
        if (attr && attr.value && attr.showInName !== false) {
          parts.push(String(attr.value).trim());
        }
      });
      return parts.filter(Boolean).join(" - ");
    },
    syncVariantName(variant) {
      if (variant.nameManual) return;
      variant.name = this.composeName(variant);
    },
    onNameManualEdit(variant) {
      variant.nameManual = true;
      if (!variant.baseName) {
        variant.baseName = variant.name;
      }
    },
    openSettings(index) {
      this.activeIndex = index;
      this.activeUid = this.variants[index] ? this.variants[index]._uid : null;
      const variant = this.variants[index];
      if (variant && !variant.initial_stocks) {
        this.$set(variant, "initial_stocks", []);
      }
      if (variant && !variant.investors) {
        this.$set(variant, "investors", []);
      }
      this.settingsTab = 0;
      this.settingsDialog = true;
    },
    pickImage(index) {
      const ref = this.$refs["variantImageInput" + index];
      const input = Array.isArray(ref) ? ref[0] : ref;
      if (input) input.click();
    },
    onImageChange(event, index) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      const variant = this.variants[index];
      variant.imageFile = file;
      if (variant.imagePreview && String(variant.imagePreview).startsWith("blob:")) {
        URL.revokeObjectURL(variant.imagePreview);
      }
      variant.imagePreview = URL.createObjectURL(file);
    },
    addBarcode(variant) {
      if (!variant.barcodes) this.$set(variant, "barcodes", []);
      variant.barcodes.push({ value: null });
    },
    removeBarcode(variant, item) {
      const i = variant.barcodes.indexOf(item);
      if (i > -1) variant.barcodes.splice(i, 1);
    },
    addInvestorRow(variant) {
      if (!variant.investors) this.$set(variant, "investors", []);
      variant.investors.push({
        investor: null,
        investorSellPercent: null,
        investorReturnSellPercent: null,
      });
    },
    removeInvestor(variant, item) {
      if (!variant.investors) return;
      const i = variant.investors.indexOf(item);
      if (i > -1) variant.investors.splice(i, 1);
    },
    changeInvestor(item) {
      if (!item.id) {
        if (item.investor) {
          item.investorSellPercent = item.investor.investorSellPercent;
          item.investorReturnSellPercent = item.investor.investorReturnSellPercent;
        } else {
          item.investorSellPercent = null;
          item.investorReturnSellPercent = null;
        }
      }
    },
    variantInitialCheck(variant) {
      if (variant && typeof variant.initialCheck === "boolean") {
        return variant.initialCheck;
      }
      return this.initialCheck;
    },
    entrepotRuls(initial) {
      return Number(initial.stock) > 0 ? [(v) => !!v || this.$t("is-required")] : [];
    },
    validateStock(initial) {
      setTimeout(function () {
        if (initial.stock < 0) {
          initial.stock = 0;
        }
      }, 50);
    },
    checkEntrepot(variant, item, j) {
      const app = this;
      const stocks = (variant && variant.initial_stocks) || [];
      if (item.entrepot && stocks.length > 1) {
        for (let i = 0; i < stocks.length; i++) {
          const value = stocks[i];
          if (
            j !== i &&
            value.entrepot &&
            value.entrepot.id &&
            value.entrepot.id === item.entrepot.id
          ) {
            setTimeout(function () {
              app.snakbarText = app.$t("selectedWarehouseIsdDuplicate");
              app.snakbarColor = "pink";
              app.snakbar = true;
              stocks[j].entrepot = null;
            }, 200);
            break;
          }
        }
      }
    },
    addInitialStock(variant) {
      if (!variant.initial_stocks) this.$set(variant, "initial_stocks", []);
      variant.initial_stocks.push({
        stock: 0,
        price: 0,
        entrepot: null,
      });
    },
    removeInitialStock(variant, i) {
      if (!variant.initial_stocks) return;
      variant.initial_stocks.splice(i, 1);
    },
    calcPriceWithTax(variant) {
      if (variant.taxPercent && variant.taxPercent > 0) {
        const taxAmount = +variant.price * (1 + +variant.taxPercent / 100);
        variant.priceWithTax = +this.roundMath(
          taxAmount,
          this.$store.state.currency.decimal
        );
      }
    },
    calcPrice(variant) {
      if (variant.taxPercent && variant.taxPercent > 0) {
        clearTimeout(this.timer);
        const app = this;
        this.timer = setTimeout(function () {
          const netPrice = +variant.priceWithTax / (1 + +variant.taxPercent / 100);
          const tax = app.roundMath(
            +variant.priceWithTax - +netPrice,
            app.$store.state.currency.decimal
          );
          variant.price = +variant.priceWithTax - +tax;
        }, 1000);
      }
    },
  },
};
</script>

<style scoped>
.variable-product-dialog__title {
  text-align: right;
}
.variable-product-dialog__title .text-body-1,
.variable-product-dialog__title .text-caption {
  text-align: right;
  display: block;
}
.variable-product-dialog__body {
  padding-top: 20px !important;
  padding-bottom: 16px !important;
  min-height: 420px;
  overflow: visible;
}
.variable-product-dialog__panel {
  min-height: 280px;
  padding-top: 8px;
  padding-bottom: 12px;
}
.variable-product-dialog__inputs-desktop {
  padding-right: 38px !important;
}
.variable-product-dialog__inputs-mobile {
  padding-right: 12px !important;
}
.variable-product-dialog ::v-deep .v-tabs-items {
  overflow: visible;
}
.variable-product-dialog ::v-deep .v-window__container {
  height: auto !important;
}
.variable-product-row {
  background: #fff;
  border: 1px solid #e3e3e3;
  border-radius: 6px;
  padding: 12px 14px;
}
.variable-product-row__main {
  gap: 0;
  min-height: 68px;
}
.variable-product-row-index {
  flex: 0 0 28px;
  width: 28px;
  height: 68px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9e9e9e;
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  user-select: none;
  line-height: 1;
}
.variable-product-thumb {
  cursor: pointer;
  flex: 0 0 auto;
  line-height: 0;
}
.variable-product-thumb__avatar {
  border: 1px solid #e0e0e0;
}
.variable-product-fields {
  flex: 1 1 auto;
  min-height: 40px;
  margin-bottom: 12px;
  align-items: center !important;
}
.variable-product-name {
  max-width: 280px;
  min-width: 200px;
  flex: 1 1 150px;
  margin-bottom: 0 !important;
}
.variable-product-name ::v-deep .v-input__control {
  min-height: 40px;
}
.variable-product-name ::v-deep .v-input__slot {
  min-height: 40px !important;
  margin-bottom: 0 !important;
}
.variable-product-price {
  max-width: 260px;
  min-width: 160px;
  flex: 0 1 180px;
  margin-bottom: 0 !important;
}
.variable-product-price ::v-deep .v-input__control {
  min-height: 40px;
}
.variable-product-price ::v-deep .v-input__slot {
  min-height: 40px !important;
  margin-bottom: 0 !important;
}
.variable-product-add-attr {
  flex: 0 0 auto;
  align-self: center;
  min-width: 132px;
  height: 40px !important;
  margin-top: 19px !important;
  margin-bottom: 0 !important;
  letter-spacing: 0;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px !important;
}
.variable-product-actions {
  flex: 0 0 auto;
  height: 40px;
  margin-bottom: 12px;
  margin-top: 4px;
  margin-inline-start: auto;
  gap: 2px;
}
.drag-handle {
  cursor: grab;
}
.drag-handle:active {
  cursor: grabbing;
}
.sortable-ghost {
  opacity: 0.45;
}
.variable-product-attrs {
  padding-top: 14px;
  padding-bottom: 10px;
  border-top: 1px dashed #e8e8e8;
  padding-left: 4px;
}
.variable-product-attrs--scroll {
  max-height: 148px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: #d0d0d0 transparent;
}
.variable-product-attrs--scroll::-webkit-scrollbar {
  width: 4px;
}
.variable-product-attrs--scroll::-webkit-scrollbar-track {
  background: transparent;
}
.variable-product-attrs--scroll::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 4px;
}
.variable-product-attrs--scroll::-webkit-scrollbar-thumb:hover {
  background: #bdbdbd;
}
.variable-product-dialog-attrs--scroll {
  max-height: 288px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: #d0d0d0 transparent;
}
.variable-product-dialog-attrs--scroll::-webkit-scrollbar {
  width: 4px;
}
.variable-product-dialog-attrs--scroll::-webkit-scrollbar-track {
  background: transparent;
}
.variable-product-dialog-attrs--scroll::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 4px;
}
.variable-product-dialog-attrs--scroll::-webkit-scrollbar-thumb:hover {
  background: #bdbdbd;
}
.variable-product-scroll-box {
  height: 170px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: #d0d0d0 transparent;
}
.variable-product-scroll-box::-webkit-scrollbar {
  width: 4px;
}
.variable-product-scroll-box::-webkit-scrollbar-track {
  background: transparent;
}
.variable-product-scroll-box::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 4px;
}
.variable-product-scroll-box::-webkit-scrollbar-thumb:hover {
  background: #bdbdbd;
}
.variable-product-attr-input {
  margin-bottom: 0 !important;
}
.variable-product-attr-input ::v-deep .v-input__control {
  min-height: 40px !important;
}
.variable-product-attr-input ::v-deep .v-input__slot {
  min-height: 40px !important;
  height: 40px !important;
}
.variable-product-attrs ::v-deep .v-row {
  min-height: 64px;
  align-items: center;
  margin-bottom: 4px !important;
}
</style>
